import React, { useEffect, useState } from "react";
import { getProjectDetails, updateProjectDetail } from "../StepperService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../helpers/methods";
import CustomLoader from "../../../../../global/components/CustomLoader/CustomLoader";
import { YhButton, YhInput } from "../../../../../ui";
import { getLocationMeta } from "../../../../MetaData/components/MetaList/components/ProjectLocationList/projectLocationService";

interface CustomProps {
  nextStep?: () => void | undefined;
  backStep?: () => void | undefined;
  handleClose: Function;
  userId: String;
  idd: string;
  setIdd: Function;
  fromSettingOrLogout?: any;
  data?: any;
}

function hasObjectIdInText(value?: string) {
  return /[a-f0-9]{24}/i.test(String(value || ""));
}

function looksLikeObjectId(value?: unknown) {
  return /^[a-f0-9]{24}$/i.test(String(value || "").trim());
}

function cleanSeoText(value?: string) {
  return String(value || "")
    .replace(/[a-f0-9]{24}/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\sin\s*\?/gi, "?")
    .replace(/\s+\?/g, "?")
    .replace(/\s+,/g, ",")
    .replace(/\s+\|/g, " |")
    .trim();
}

function cityFromValue(value?: unknown): string {
  if (value == null) return "";
  if (typeof value === "object") {
    const named = String(
      (value as { name?: string; city_name?: string }).name ||
        (value as { city_name?: string }).city_name ||
        ""
    ).trim();
    return named && !hasObjectIdInText(named) ? named : "";
  }
  const text = String(value).trim();
  if (!text || hasObjectIdInText(text) || looksLikeObjectId(text)) return "";
  return text;
}

function displayCity(data?: any) {
  return (
    cityFromValue(data?.city_name) ||
    cityFromValue(data?.cityName) ||
    cityFromValue(data?.city)
  );
}

function safeText(value?: string) {
  const text = String(value || "").trim();
  return text && !hasObjectIdInText(text) ? text : "";
}

function unwrapProject(raw: any): any {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw[0] || null;
  if (raw.data && (raw.data.project_name || raw.data._id || raw.data.city)) {
    return unwrapProject(raw.data);
  }
  return raw;
}

async function resolveCityFromLocationMeta(data?: any): Promise<string> {
  const known = displayCity(data);
  if (known) return known;
  const cityId = String(data?.city || "").trim();
  if (!cityId || !looksLikeObjectId(cityId)) return "";
  try {
    const res = await getLocationMeta();
    const countries = Array.isArray(res?.data) ? res.data : [];
    const preferred =
      countries.find(
        (country: any) =>
          String(country?._id) === String(data?.countryId)
      ) || null;
    const searchOrder = preferred
      ? [preferred, ...countries.filter((c: any) => c !== preferred)]
      : countries;
    for (const country of searchOrder) {
      for (const state of country?.states || []) {
        const city = (state?.cities || []).find(
          (item: any) =>
            String(item?._id) === cityId || String(item?.name) === cityId
        );
        const name = String(city?.name || "").trim();
        if (name && !looksLikeObjectId(name)) return name;
      }
    }
  } catch {
    // Keep the form from showing a raw ObjectId if location meta is unavailable.
  }
  return "";
}

function buildSeoFallback(data?: any) {
  const projectName = safeText(data?.project_name);
  const city = displayCity(data);
  const address = safeText(data?.fullAddress);
  const zone = safeText(data?.zone);
  const status = safeText(data?.propertyStatus) || "project";
  const unit = safeText(data?.priceList?.[0]?.bedroom) || "2 BHK";
  const inCity = city ? ` in ${city}` : "";
  const title = `${[projectName, zone, address].filter(Boolean).join(" ")} | Yhataw Realty`;
  const description = `Looking for a signature ${unit}${inCity}? ${projectName} ${address} is a ${status} offering modern homes with top-class amenities. Check floor plans, location insights, photos, videos, and RERA details before you decide with Yhataw Realty.`;
  return {
    metaTitle: cleanSeoText(title.replace(/\s+/g, " ")),
    metaDescription: cleanSeoText(description.replace(/\s+/g, " ")),
  };
}

function settleSeo(primary?: any, secondary?: any) {
  const source = { ...(secondary || {}), ...(primary || {}) };
  const generated = buildSeoFallback(source);
  const rawTitle = String(primary?.metaTitle || secondary?.metaTitle || "");
  const rawDesc = String(
    primary?.metaDescription || secondary?.metaDescription || ""
  );
  return {
    metaTitle:
      !rawTitle.trim() || hasObjectIdInText(rawTitle)
        ? generated.metaTitle
        : cleanSeoText(rawTitle),
    metaDescription:
      !rawDesc.trim() || hasObjectIdInText(rawDesc)
        ? generated.metaDescription
        : cleanSeoText(rawDesc),
  };
}

const Seo = (props: CustomProps) => {
  const [seo, setSeo] = useState({
    metaTitle: "",
    metaDescription: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const projectDetails = async () => {
      setLoading(true);
      setSeo({ metaTitle: "", metaDescription: "" });

      let source = unwrapProject(props.data);

      if (props?.idd) {
        try {
          const response = await getProjectDetails(props.idd);
          if (cancelled) return;
          const fetched = unwrapProject(response?.data);
          if (fetched) {
            source = { ...(source || {}), ...fetched };
          }
        } catch (error: any) {
          if (cancelled) return;
          if (!source) {
            openErrorNotification(
              error?.message || "Failed to load SEO details"
            );
          }
        }
      }

      if (cancelled) return;

      const cityName = await resolveCityFromLocationMeta(source);
      if (cancelled) return;
      if (cityName) {
        source = { ...(source || {}), city_name: cityName, city: cityName };
      } else if (source) {
        source = {
          ...source,
          city: looksLikeObjectId(source.city) ? "" : source.city,
        };
      }

      const settled = settleSeo(source);
      setSeo({
        metaTitle: hasObjectIdInText(settled.metaTitle)
          ? cleanSeoText(settled.metaTitle)
          : settled.metaTitle,
        metaDescription: hasObjectIdInText(settled.metaDescription)
          ? cleanSeoText(settled.metaDescription)
          : settled.metaDescription,
      });
      setLoading(false);
    };

    projectDetails();
    return () => {
      cancelled = true;
    };
  }, [props?.idd]);

  const submitForm = async () => {
    try {
      if (!props?.idd) {
        openErrorNotification("Please save project information first");
        return;
      }
      setSaving(true);
      const payload = {
        _id: props.idd,
        metaTitle: cleanSeoText(seo.metaTitle),
        metaDescription: cleanSeoText(seo.metaDescription),
        metaManual: true,
      };
      const response = await updateProjectDetail(payload);
      if (response?.success) {
        openSuccessNotification(response?.message || "SEO saved");
        props.nextStep?.();
      } else {
        openErrorNotification(response?.message || "Failed to save SEO");
      }
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to save SEO");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative w-full">
      {loading ? <CustomLoader apiResponse /> : null}

      <div className="mb-6 flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EEF0FF] text-sm font-bold text-[#A02448]">
          ☰
        </span>
        <div>
          <h2 className="text-lg font-bold tracking-tight text-[#111827]">
            SEO
          </h2>
          <p className="mt-0.5 text-sm text-[#6B7280]">
            {props.fromSettingOrLogout === "fromSettingOrLogout"
              ? "Update the SEO details of the project"
              : "Add SEO details of the project"}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-2xl border border-[#EEF0F5] bg-[#FCFCFD]">
          <span className="inline-block h-9 w-9 animate-spin rounded-full border-[3px] border-[#E8D0D8] border-t-[#A02448]" />
          <p className="text-sm font-medium text-[#6B7280]">
            Loading SEO details…
          </p>
          <p className="text-xs text-[#9CA3AF]">
            Waiting for project and location data to settle
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            <YhInput
              label="Meta Title"
              placeholder="Enter Meta Title"
              value={seo.metaTitle}
              onChange={(e) =>
                setSeo((prev) => ({
                  ...prev,
                  metaTitle: e.target.value,
                }))
              }
            />
            <YhInput
              label="Meta Description"
              placeholder="Enter Meta Description"
              multiline
              rows={5}
              value={seo.metaDescription}
              onChange={(e) =>
                setSeo((prev) => ({
                  ...prev,
                  metaDescription: e.target.value,
                }))
              }
            />
          </div>

          <div className="mt-8 flex items-center justify-between gap-3 border-t border-[#EEF0F5] pt-5">
            <YhButton variant="secondary" onClick={() => props.backStep?.()}>
              ← Back
            </YhButton>
            <YhButton
              variant="primary"
              disabled={saving}
              onClick={submitForm}
            >
              {saving ? "Saving..." : "Save"}
            </YhButton>
          </div>
        </>
      )}
    </div>
  );
};

export default Seo;
